/**
 * graphicsSettings.ts — Runtime graphics quality knobs
 *
 * BETA 16.59 — Wave 4 of the mega settings push. Per Dylan: "any ways
 * to have better optimix=zation in the game so there is never items
 * popping in game or lag, or stutters?" + "there is no resolution
 * changer or graphics settings in the pause menu when gaming."
 *
 * This module holds the LIVE values that engine + render code reads.
 * Persistence is handled by userSettings.ts; on load, the host calls
 * applyGraphicsSettings() to push the persisted values in here.
 *
 * Why a separate module? Engine + render code is hot-path. Reading from
 * a React state object would require a prop drill or a context lookup.
 * A simple module-level state with cheap getters keeps the hot path
 * branch-free + cache-friendly.
 */

export type ParticleDensity = 'off' | 'low' | 'medium' | 'high';

// BETA 16.64 — Resolution preset (Dylan: "1080p, 2k, 4k, 720p, and it
// auto chose"). The numeric resolutionScale is now derived from the
// preset + current viewport, recomputed on viewport changes. AUTO picks
// based on device power + network — see autoResolution.ts.
import type { ResolutionPreset } from './autoResolution';
import { presetToScale } from './autoResolution';
import { setDebugDynamicLighting } from './debugState';
export type { ResolutionPreset };

interface GraphicsState {
  particleDensityMult: number;     // 0 / 0.33 / 0.66 / 1.0 — multiplier on spawn counts
  particleDensity: ParticleDensity;
  resolutionPreset: ResolutionPreset;
  resolutionScale: number;         // derived from preset + viewport (0.5..1.0)
  screenShakeEnabled: boolean;     // false → shake calls are no-ops
  glowEffectsEnabled: boolean;     // false → skip ctx.shadowBlur paths
  screenFlashEnabled: boolean;     // false → lastStandFlashTimer + similar render passes skip
  dynamicLightingEnabled: boolean; // BETA 20.99 — true → engine paints the dark "night" + player glow layer. Default OFF.
}

const state: GraphicsState = {
  particleDensityMult: 1.0,
  particleDensity: 'high',
  resolutionPreset: 'auto',
  resolutionScale: 1.0,
  screenShakeEnabled: true,
  glowEffectsEnabled: true,
  screenFlashEnabled: true,
  dynamicLightingEnabled: false,
};

const DENSITY_MAP: Record<ParticleDensity, number> = {
  off:    0,
  low:    0.33,
  medium: 0.66,
  high:   1.0,
};

export function setParticleDensity(d: ParticleDensity): void {
  state.particleDensity = d;
  state.particleDensityMult = DENSITY_MAP[d];
}
export function getParticleDensity(): ParticleDensity { return state.particleDensity; }
export function getParticleDensityMult(): number { return state.particleDensityMult; }

export function setResolutionScale(s: number): void {
  // Clamp to a sane range — anything below 0.5 is unreadable, above 1.5 will
  // tank perf on large monitors.
  state.resolutionScale = Math.max(0.5, Math.min(1.5, s));
}
export function getResolutionScale(): number { return state.resolutionScale; }

// BETA 16.64 — Preset accessors. setResolutionPreset stores the preset
// AND recomputes the underlying scale from the current viewport. Call
// recomputeResolutionScale() any time the viewport changes (resize,
// fullscreen toggle) so the canvas adjusts.
export function setResolutionPreset(p: ResolutionPreset): void {
  state.resolutionPreset = p;
  recomputeResolutionScale();
}
export function getResolutionPreset(): ResolutionPreset { return state.resolutionPreset; }
export function recomputeResolutionScale(): void {
  if (typeof window === 'undefined') return;
  const w = window.innerWidth || 1920;
  const h = window.innerHeight || 1080;
  state.resolutionScale = presetToScale(state.resolutionPreset, w, h);
}

export function setScreenShakeEnabled(on: boolean): void { state.screenShakeEnabled = on; }
export function getScreenShakeEnabled(): boolean { return state.screenShakeEnabled; }

export function setGlowEffectsEnabled(on: boolean): void { state.glowEffectsEnabled = on; }
export function getGlowEffectsEnabled(): boolean { return state.glowEffectsEnabled; }

export function setScreenFlashEnabled(on: boolean): void { state.screenFlashEnabled = on; }
export function getScreenFlashEnabled(): boolean { return state.screenFlashEnabled; }

// BETA 20.99 — Dynamic lighting moved from the dev-only debug panel into the
// player Settings (Dylan plays on a handheld; backtick is awkward). This is the
// PERSISTENT source of truth; it bridges to the engine's debugDynamicLighting
// flag (which the renderer reads) so the dev panel toggle keeps working too.
export function setDynamicLightingEnabled(on: boolean): void {
  state.dynamicLightingEnabled = on;
  setDebugDynamicLighting(on);
}
export function getDynamicLightingEnabled(): boolean { return state.dynamicLightingEnabled; }

/** Apply a UserSettings snapshot at app boot or after Settings panel
 *  closes. Tolerant of partial inputs — anything missing keeps current. */
export interface PartialGraphicsInput {
  particleDensity?: ParticleDensity;
  resolutionPreset?: ResolutionPreset;
  resolutionScale?: number;
  screenShakeEnabled?: boolean;
  glowEffectsEnabled?: boolean;
  screenFlashEnabled?: boolean;
  dynamicLightingEnabled?: boolean;
}
export function applyGraphicsSettings(input: PartialGraphicsInput): void {
  if (input.particleDensity != null) setParticleDensity(input.particleDensity);
  // BETA 16.64 — preset takes precedence over the legacy numeric scale.
  // If both are supplied, preset wins (and scale is recomputed from it).
  if (input.resolutionPreset != null) setResolutionPreset(input.resolutionPreset);
  else if (typeof input.resolutionScale === 'number') setResolutionScale(input.resolutionScale);
  if (typeof input.screenShakeEnabled === 'boolean') setScreenShakeEnabled(input.screenShakeEnabled);
  if (typeof input.glowEffectsEnabled === 'boolean') setGlowEffectsEnabled(input.glowEffectsEnabled);
  if (typeof input.screenFlashEnabled === 'boolean') setScreenFlashEnabled(input.screenFlashEnabled);
  if (typeof input.dynamicLightingEnabled === 'boolean') setDynamicLightingEnabled(input.dynamicLightingEnabled);
}
